import type { JSX } from 'solid-js';
import { createEffect, createSignal, Show } from 'solid-js';
import { t } from '../i18n';
import type { SeatInfo } from '../lib/billing';
import type { EnrollRefusal } from '../lib/enroll-refusal';
import { type ConnectPlan, connectPlan, refusalPlan } from '../lib/fleet-connect';
import { ConnectMachineBody } from './ConnectMachineBody';
import { ConnectPlanLine } from './ConnectPlanLine';
import { Modal } from './Modal';

// The Connect a machine dialog (#6567, replacing the add-a-box dialog). The two
// ways to connect live in <ConnectMachineBody/>, which the donate wizard mounts
// as well — this file is the dialog SHELL and its plan line, nothing else.
// Closing it disposes both transport panels, which is where a revealed one-time
// code's plaintext dies.
//
// THE PLAN LINE rides the shell rather than either panel (`connectPlan`,
// `lib/fleet-connect.ts`): BEFORE a mint it counts the machine about to connect
// ("This one makes X+1"); AFTER one it reads the Fleet page's RE-READ
// entitlements as they are, because the server already counts the pending code.
// `minted` is "something was minted or queued since this dialog opened", and a
// close re-arms it. An org at a limit of 0 gets <OrgUnfundedAlert/> in its place,
// never "0 of 0". A refused mint about the PLAN (`seat_cap`, `org_unfunded`) is
// lifted here from either transport (`onRefusal`) and REPLACES the line, so the
// dialog says it once: the limit sentence, or one <OrgUnfundedAlert/>, never
// "This one makes X+1" above a panel saying the opposite. The mint itself is an explicit "Create setup command" click
// inside the token panel (owner ruling G15): opening the dialog mints nothing.

export type ConnectMachineModalProps = {
  readonly open: boolean;
  readonly token: string | null;
  readonly onClose: () => void;
  /** Called after a successful mint / queued bootstrap so the parent can refetch. */
  readonly onMinted: () => void;
  /** The entitlements read for the plan line; undefined while in flight or failed. */
  readonly seat?: SeatInfo | undefined;
  /** The account is an org with a limit of 0 (`isOrgUnfunded`, `@developerz/domain`). */
  readonly orgUnfunded?: boolean | undefined;
};

export function ConnectMachineModal(props: ConnectMachineModalProps): JSX.Element {
  const [minted, setMinted] = createSignal(false);
  /** The last refusal a transport reported this visit; a plan refusal is said HERE, once. */
  const [refusal, setRefusal] = createSignal<EnrollRefusal | null>(null);
  createEffect(() => {
    if (!props.open) {
      setMinted(false);
      setRefusal(null);
    }
  });
  // WHEN THE LATE-MINT GUARANTEE HOLDS. A success that lands while the dialog is
  // CLOSED still refetches (its seat is real) and does not flip the next visit's
  // line to "after": `minted` only moves while `open` is true, and a close re-arms
  // it. A success that lands after a close AND a reopen inside its flight window
  // reads `open` true again, so it DOES flip the reopened visit's line to "after".
  // That line is still the server's re-read count, never a guess, so it reads
  // "X of Y" where "This one makes X+1" was due: the cost is one sentence, never a
  // wrong number. The token itself is still dropped there (`EnrollTransportToken`'s
  // session counter); the SSH panel keeps no such counter.
  const enrolled = (): void => {
    if (props.open) setMinted(true);
    props.onMinted();
  };
  // Refusals follow the same rule: one reported while closed never reaches the next
  // visit. The token panel drops a late refusal itself; this guards the SSH panel.
  const refused = (next: EnrollRefusal | null): void => {
    if (props.open) setRefusal(next);
  };
  const plan = (): ConnectPlan | null => {
    const seat = props.seat;
    return seat === undefined
      ? refusalPlan(refusal())
      : connectPlan(seat, {
          orgUnfunded: props.orgUnfunded === true,
          minted: minted(),
          refusal: refusal(),
        });
  };
  return (
    <Modal
      open={props.open}
      title={t('fleet.connect.title')}
      desc={t('fleet.connect.intro')}
      size="lg"
      onClose={props.onClose}
    >
      <Show when={plan()}>{(p) => <ConnectPlanLine plan={p()} />}</Show>
      <ConnectMachineBody
        open={props.open}
        token={props.token}
        onEnrolled={enrolled}
        onRefusal={refused}
        onClose={props.onClose}
      />
    </Modal>
  );
}
